import type {
  CommentReactionSummaryData,
  Reaction,
  ReactionBreakdown,
  ReactionSummaryData,
  ReactionType,
} from "@/lib/humhub-api/types";

type BaseReactionSummary = {
  current_user_emotion: ReactionType | null;
  total?: number;
  last_reactions?: Reaction[];
  by_emotion?: ReactionBreakdown;
};

export const computeReactionBreakdownAfterChange = (
  existing: ReactionBreakdown | undefined,
  previous: ReactionType | null,
  next: ReactionType | null,
): ReactionBreakdown | undefined => {
  if (!existing && !previous && !next) {
    return undefined;
  }

  const updated: ReactionBreakdown = { ...(existing ?? {}) };
  if (previous) {
    updated[previous] = Math.max(0, (updated[previous] ?? 0) - 1);
  }
  if (next) {
    updated[next] = (updated[next] ?? 0) + 1;
  }
  return updated;
};

const computeReactionTotalAfterChange = (
  previous: ReactionType | null,
  next: ReactionType | null,
  currentTotal: number | undefined,
) => {
  const safeTotal = currentTotal ?? 0;
  if (previous && !next) return Math.max(0, safeTotal - 1);
  if (!previous && next) return safeTotal + 1;
  return safeTotal;
};

const computeLastReactionsAfterChange = (
  existing: Reaction[] | undefined,
  next: ReactionType | null,
  currentUserId?: number,
): Reaction[] | undefined => {
  if (typeof currentUserId !== "number") {
    return existing;
  }

  const filtered = (existing ?? []).filter(
    (entry) => entry.created_by !== currentUserId,
  );
  if (!next) {
    return filtered;
  }

  const synthetic: Reaction = {
    id: Date.now(),
    created_by: currentUserId,
    emotion: next,
    created_at: new Date().toISOString(),
  };
  return [synthetic, ...filtered].slice(0, 10);
};

export const applyReactionSummaryChange = <
  T extends BaseReactionSummary | undefined,
>(
  summary: T,
  nextReaction: ReactionType | null,
  currentUserId?: number,
): T extends undefined ? CommentReactionSummaryData : T => {
  const previousReaction = summary?.current_user_emotion ?? null;

  const updated = {
    ...(summary ?? { current_user_emotion: null }),
    current_user_emotion: nextReaction,
    total: computeReactionTotalAfterChange(
      previousReaction,
      nextReaction,
      summary?.total,
    ),
    last_reactions: computeLastReactionsAfterChange(
      summary?.last_reactions,
      nextReaction,
      currentUserId,
    ),
    by_emotion: computeReactionBreakdownAfterChange(
      summary?.by_emotion,
      previousReaction,
      nextReaction,
    ),
  };

  return updated as T extends undefined ? CommentReactionSummaryData : T;
};

export const createEmptyReactionSummary = (): ReactionSummaryData => ({
  total: 0,
  current_user_emotion: null,
  last_reactions: [],
  by_emotion: {},
});
